import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { SidebarPortal, BlockDataForm } from '@plone/volto/components';

import SiteSpotlightView from './SiteSpotlightView';
import schema from './schema';
import { normalizeSingleSelectionField } from './siteSpotlight.helpers';
import useSiteSpotlightContent from './useSiteSpotlightContent';

import './styles.less';

export default function SiteSpotlightEdit(props: any) {
  const { data = {}, block, onChangeBlock, selected } = props;

  const latestDataRef = useRef(data);

  useEffect(() => {
    latestDataRef.current = data;
  }, [data]);

  const blockSchema = useMemo(() => schema(props), [props]);

  useSiteSpotlightContent({
    data,
    block,
    loadParent: true,
    loadImageOverride: false,
  });

  const mergeAndSave = useCallback(
    (patch: Record<string, any>) => {
      const currentData = latestDataRef.current || {};

      onChangeBlock(block, {
        ...currentData,
        ...patch,
      });
    },
    [block, onChangeBlock],
  );

  const handleFormBlockChange = useCallback(
    (...args: any[]) => {
      if (!args.length) return;

      if (
        args.length === 2 &&
        typeof args[0] === 'string' &&
        args[0] === block &&
        args[1] &&
        typeof args[1] === 'object'
      ) {
        const [, nextData] = args;
        const currentData = latestDataRef.current || {};

        onChangeBlock(block, {
          ...currentData,
          ...nextData,
        });

        return;
      }

      if (args.length === 1 && args[0] && typeof args[0] === 'object') {
        mergeAndSave(args[0]);
        return;
      }

      if (args.length === 2 && typeof args[0] === 'string') {
        const [fieldId, value] = args;

        mergeAndSave({
          [fieldId]: normalizeSingleSelectionField(fieldId, value),
        });

        return;
      }

      const maybeData = args[args.length - 1];

      if (maybeData && typeof maybeData === 'object') {
        mergeAndSave(maybeData);
      }
    },
    [block, onChangeBlock, mergeAndSave],
  );

  return (
    <>
      <SiteSpotlightView {...props} isEditMode />

      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={blockSchema}
          title={blockSchema.title}
          onChangeField={(id: string, value: any) =>
            mergeAndSave({
              [id]: normalizeSingleSelectionField(id, value),
            })
          }
          onChangeBlock={handleFormBlockChange}
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
}
